import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Box, Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { Button, Input, IconButton } from '@/components/ui';
import {
  Crate,
  CRATE_MAX_SLOTS,
  getCrates,
  createCrate,
  renameCrate,
  deleteCrate,
} from '@/services/crates.api';

const Crates: React.FC = () => {
  const [crates, setCrates] = useState<Crate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  const loadCrates = useCallback(async () => {
    setIsLoading(true);
    try {
      setCrates(await getCrates());
    } catch {
      console.error('Failed to load crates');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCrates();
  }, [loadCrates]);

  const handleCreate = async () => {
    const trimmed = newName.trim();
    if (trimmed.length === 0) return;
    setIsCreating(true);
    try {
      const created = await createCrate(trimmed);
      setCrates(prev => [created, ...prev]);
      setNewName('');
    } catch {
      console.error('Failed to create crate');
    } finally {
      setIsCreating(false);
    }
  };

  const startEditing = (crate: Crate) => {
    setEditingId(crate.id);
    setEditingName(crate.name);
  };

  const handleRename = async (crateId: string) => {
    const trimmed = editingName.trim();
    if (trimmed.length === 0) return;
    try {
      const updated = await renameCrate(crateId, trimmed);
      setCrates(prev => prev.map(item => (item.id === crateId ? { ...item, name: updated.name } : item)));
      setEditingId(null);
    } catch {
      console.error('Failed to rename crate');
    }
  };

  const handleDelete = async (crate: Crate) => {
    if (!window.confirm(`Delete crate "${crate.name}"?`)) return;
    try {
      await deleteCrate(crate.id);
      setCrates(prev => prev.filter(item => item.id !== crate.id));
    } catch {
      console.error('Failed to delete crate');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Box className="w-7 h-7 text-meradio-primary" />
        <h1 className="text-3xl font-bold text-meradio-dark dark:text-text-dark-primary">Crates</h1>
      </div>

      <div className="flex items-center gap-2 mb-8 max-w-md">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleCreate();
          }}
          placeholder="New crate name"
          aria-label="New crate name"
          maxLength={60}
        />
        <Button
          variant="primary"
          onClick={handleCreate}
          isLoading={isCreating}
          disabled={newName.trim().length === 0}
        >
          <Plus className="w-4 h-4" />
          New crate
        </Button>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-32 rounded-xl bg-neutral-200 dark:bg-bg-dark-secondary animate-pulse" />
          ))}
        </div>
      ) : crates.length === 0 ? (
        <div className="text-center py-16">
          <Box className="w-16 h-16 text-neutral-300 dark:text-text-dark-tertiary mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-neutral-600 dark:text-text-dark-primary mb-2">
            No crates yet
          </h3>
          <p className="text-neutral-500 dark:text-text-dark-secondary">
            Dig through your videos and start filling a crate.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {crates.map((crate) => (
            <div
              key={crate.id}
              className="group relative rounded-xl bg-white dark:bg-bg-dark-secondary border border-meradio-light dark:border-border-dark p-5 shadow-sm hover:shadow-md transition-shadow"
            >
              {editingId === crate.id ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleRename(crate.id);
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                    aria-label="Crate name"
                    maxLength={60}
                    autoFocus
                  />
                  <IconButton
                    onClick={() => handleRename(crate.id)}
                    variant="ghost"
                    size="sm"
                    aria-label="Save name"
                    className="text-meradio-primary"
                  >
                    <Check className="w-4 h-4" />
                  </IconButton>
                  <IconButton
                    onClick={() => setEditingId(null)}
                    variant="ghost"
                    size="sm"
                    aria-label="Cancel rename"
                    className="text-meradio-dark-light dark:text-text-dark-secondary"
                  >
                    <X className="w-4 h-4" />
                  </IconButton>
                </div>
              ) : (
                <>
                  <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <IconButton
                      onClick={() => startEditing(crate)}
                      variant="ghost"
                      size="sm"
                      aria-label="Rename crate"
                      className="text-meradio-dark-light dark:text-text-dark-secondary hover:text-meradio-primary"
                    >
                      <Pencil className="w-4 h-4" />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(crate)}
                      variant="ghost"
                      size="sm"
                      aria-label="Delete crate"
                      className="text-meradio-dark-light dark:text-text-dark-secondary hover:text-danger-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </IconButton>
                  </div>

                  <Link to={`/crates/${crate.id}`} className="block">
                    <div className="w-12 h-12 rounded-lg bg-meradio-primary/10 flex items-center justify-center mb-4">
                      <Box className="w-6 h-6 text-meradio-primary" />
                    </div>
                    <h3 className="text-lg font-semibold text-meradio-dark dark:text-text-dark-primary truncate pr-16">
                      {crate.name}
                    </h3>
                    <p className="text-sm text-meradio-dark-light dark:text-text-dark-secondary mt-1 tabular-nums">
                      {crate.videoCount}/{CRATE_MAX_SLOTS} records
                    </p>
                  </Link>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Crates;
